import { authStoreActions, globalAuthStore } from '@stores/bootstrap-stores/auth';
import { saveReturnUrl } from '@utils/auth/returnUrl';
import { log } from '@utils/log';
import { trimSlashes } from '@utils/strTransform';

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  credentials?: RequestCredentials;
  skipBearerToken?: boolean;
  retryOnUnauthorized?: boolean;
};

class ApiError extends Error {
  status: number;
  payload?: unknown;

  constructor(message: string, status: number, payload?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

// Backend routes are rooted at /v1 (public) and /admin (private); Firebase Hosting rewrites forward paths unmodified.
const defaultApiBase = '';
let isHandlingSessionExpired = false;

function joinUrl(base: string, path: string): string {
  return `${trimSlashes(base)}/${trimSlashes(path)}`;
}

function buildRequestUrl(
  path: string,
  query?: Record<string, string | number | boolean | undefined | null>
): URL {
  const isAbsolutePath = /^https?:\/\//i.test(path);
  const selectedApiBase = defaultApiBase;

  const resolvedPath = isAbsolutePath
    ? path
    : selectedApiBase
      ? joinUrl(selectedApiBase, path)
      : path;

  const url = new URL(resolvedPath, window.location.origin);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      url.searchParams.set(key, String(value));
    });
  }

  return url;
}

function showSessionExpired(): void {
  const returnUrl = window.location.pathname + window.location.search;

  if (returnUrl.startsWith('/__/auth')) {
    return;
  }

  if (returnUrl && returnUrl !== '/') {
    saveReturnUrl(returnUrl, globalAuthStore.getState().authUser?.uid ?? null);
  }

  if (isHandlingSessionExpired) {
    return;
  }

  isHandlingSessionExpired = true;
  window.location.assign('/__/auth');
}

// On a fresh page load, Firebase Auth restores the persisted session asynchronously; a request
// firing before onIdTokenChanged resolves would otherwise be misread as "no session".
function waitForAuthResolved(timeoutMs = 5000): Promise<void> {
  if (globalAuthStore.getState().isAuthResolved) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      unsubscribe();
      resolve();
    }, timeoutMs);

    const unsubscribe = globalAuthStore.subscribe(
      (state) => state.isAuthResolved,
      (isAuthResolved) => {
        if (isAuthResolved) {
          clearTimeout(timeout);
          unsubscribe();
          resolve();
        }
      }
    );
  });
}

async function parsePayload(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  return isJson ? await response.json() : await response.text();
}

async function executeRequest(
  url: URL,
  method: RequestOptions['method'],
  headers: HeadersInit,
  body: unknown,
  credentials: RequestCredentials
): Promise<Response> {
  return fetch(url.toString(), {
    method,
    headers,
    credentials,
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
}

function requestFactory() {
  return async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
    const {
      method = 'GET',
      body,
      query,
      credentials = 'same-origin',
      skipBearerToken = false,
      retryOnUnauthorized = true
    } = options;

    const url = buildRequestUrl(path, query);

    log.info(`${method} ${url.pathname}`);

    if (!skipBearerToken) {
      await waitForAuthResolved();
    }

    const token = globalAuthStore.getState().token;
    const headers: HeadersInit = {
      Accept: 'application/json'
    };

    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    if (!skipBearerToken && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (!skipBearerToken && !token) {
      showSessionExpired();
      throw new ApiError('Authentication token is missing', 401);
    }

    let response = await executeRequest(url, method, headers, body, credentials);

    if (!skipBearerToken && response.status === 401 && retryOnUnauthorized) {
      try {
        const refreshedToken = await authStoreActions().getRefreshToken();

        if (!refreshedToken) {
          showSessionExpired();
          throw new ApiError('Failed to refresh token', 401);
        }

        headers.Authorization = `Bearer ${refreshedToken}`;
        response = await executeRequest(url, method, headers, body, credentials);
      } catch (refreshError) {
        log.error('Token refresh failed', refreshError);
        showSessionExpired();
        throw refreshError;
      }
    }

    const payload = await parsePayload(response);

    if (!response.ok) {
      if (!skipBearerToken && response.status === 401) {
        showSessionExpired();
      }

      throw new ApiError(
        `Request failed: ${response.status} ${response.statusText}`,
        response.status,
        payload
      );
    }

    return payload as T;
  };
}

export const requestService = requestFactory();
